import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, hasModuleAccess, hasRole } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import LogoBrand from "./LogoBrand";
import LanguageSwitch from "./LanguageSwitch";
import { Button } from "./ui/button";
import {
  LayoutDashboard, Boxes, ClipboardList, DoorOpen, WashingMachine,
  Warehouse, LogOut, Menu, X, TriangleAlert, ReceiptText, LineChart,
  Settings, ShieldCheck, ClipboardCheck, User, ChevronDown, Scale,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", i18n: "nav.dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/assets/master", i18n: "nav.assets", icon: Boxes, module: "asset_management" },
  { to: "/assets/maintenance", i18n: "nav.maintenance", icon: Boxes, roles: ["admin","accounts","hk_supervisor","maintenance"] },
  { to: "/assets/warranty", i18n: "nav.warranty", icon: ShieldCheck, roles: ["admin","accounts","hk_supervisor","maintenance"] },
  { to: "/master-stock", i18n: "nav.master_stock", icon: Boxes, module: "master_linen_stock" },
  { to: "/housekeeping", i18n: "nav.housekeeping", icon: ClipboardList, roles: ["admin","accounts","hk_supervisor","hk_staff","front_office"] },
  { to: "/rooms", i18n: "nav.rooms", icon: DoorOpen, roles: ["admin","accounts","hk_supervisor","hk_staff","front_office"] },
  { to: "/laundry", i18n: "nav.laundry", icon: WashingMachine, roles: ["admin","accounts","hk_supervisor","hk_staff","laundry","front_office"] },
  { to: "/store", i18n: "nav.store", icon: Warehouse, module: "store_inventory" },
  { to: "/checkout", i18n: "nav.checkout", icon: ClipboardCheck, roles: ["admin","accounts","hk_supervisor","hk_staff","front_office"] },
  { to: "/missing-damaged", i18n: "nav.missing_damaged", icon: TriangleAlert, roles: ["admin","accounts","hk_supervisor","hk_staff","laundry","front_office"] },
  { to: "/discrepancies", i18n: "nav.discrepancies", icon: Scale, roles: ["admin","accounts","hk_supervisor"] },
  { to: "/purchases", i18n: "nav.purchases", icon: ReceiptText, module: "purchase_register" },
  { to: "/consumption", i18n: "nav.consumption", icon: LineChart, roles: ["admin","accounts","hk_supervisor"] },
  { to: "/closing", i18n: "nav.closing", icon: ShieldCheck, roles: ["admin","accounts","hk_supervisor"] },
];

const ADMIN_NAV = [
  { to: "/admin", i18n: "nav.admin", icon: Settings, roles: ["admin","accounts","hk_supervisor"] },
  { to: "/audit", i18n: "nav.audit", icon: ShieldCheck, module: "audit_log" },
];

function NavItem({ item, onClick, t }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.to.replace("/", "")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm border-l-3 border-transparent transition-colors ${isActive ? "active" : "text-slate-300"}`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{t(item.i18n)}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);

  const canSee = item => (!item.roles || hasRole(user, ...item.roles))
    && (!item.module || hasModuleAccess(user, item.module));
  const visibleMain = NAV.filter(canSee);
  const visibleAdmin = ADMIN_NAV.filter(canSee);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar desktop */}
      <aside className="vr-sidebar hidden md:flex md:flex-col w-64 shrink-0 sticky top-0 h-screen no-print">
        <div className="p-4 border-b border-slate-800 flex items-center gap-3">
          <LogoBrand variant="sidebar" />
          <div>
            <div className="font-serif text-lg leading-tight text-white">{t("brand")}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{t("brand_sub")}</div>
          </div>
        </div>
        <nav className="sidebar-navigation flex-1 overflow-y-auto py-3">
          {visibleMain.map((n) => <NavItem key={n.to} item={n} t={t} />)}
          {visibleAdmin.length > 0 && (
            <>
              <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{t("nav.system")}</div>
              {visibleAdmin.map((n) => <NavItem key={n.to} item={n} t={t} />)}
            </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800 text-xs text-slate-400 space-y-3">
          <div>
            <div className="mb-2 flex items-center gap-2"><User className="w-3.5 h-3.5" /> {user?.name}</div>
            <div className="text-slate-500">{user?.role_label || user?.role}</div>
          </div>
          <LanguageSwitch variant="sidebar" />
          <Button
            data-testid="logout-btn"
            variant="outline" size="sm" className="w-full bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> {t("auth.sign_out")}
          </Button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top nav mobile */}
        <header className="md:hidden vr-sidebar flex items-center justify-between px-4 py-3 no-print">
          <div className="flex items-center gap-2">
            <LogoBrand variant="sidebar" />
            <div className="font-serif text-white">{t("brand")}</div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitch variant="sidebar" />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpenMobile(!openMobile)}
              className="p-2 text-white"
            >
              {openMobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </header>
        {openMobile && (
          <div className="md:hidden vr-sidebar no-print">
            {visibleMain.map((n) => <NavItem key={n.to} item={n} t={t} onClick={() => setOpenMobile(false)} />)}
            {visibleAdmin.map((n) => <NavItem key={n.to} item={n} t={t} onClick={() => setOpenMobile(false)} />)}
            <div className="p-3 border-t border-slate-800">
              <Button
                data-testid="logout-btn-mobile" variant="outline" size="sm"
                className="w-full bg-transparent border-slate-700 text-slate-200"
                onClick={async () => { await logout(); nav("/login"); }}
              >{t("auth.sign_out")}</Button>
            </div>
          </div>
        )}

        <main className="flex-1 p-4 md:p-8 max-w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
